import React, { ReactNode, useCallback, useContext, useMemo } from 'react';
import LeadsContext from '../../providers/Leads/Leads.context';
import useQueryParams from 'hooks/useQueryParams';
import useScrollIntoView from 'hooks/useScrollIntoView';
import { useLockScrollOnParamsReroute } from 'hooks/useLockScrollOnParamsReroute';
import classNames from 'classnames';
import {
  SORTING_VALUES,
  HEADING_TRANSLATIONS,
  CUSTOM_COL_SIZES,
  TABLE_COLUMNS,
  SUPER_ADMIN_COLUMNS,
  REQUIRED_COLUMN,
} from './data';
import { BODY_CONTENT_GENERATE } from './components/LeadsTableCell';
import ReorderResizeTable from 'components/ReorderResizeTable';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { Lead } from 'models/Lead';
import ShowInactiveLeads from '../ShowInactiveLeads';
import {
  INACTIVE_PAGE,
  ONGOING_PAGE,
} from '../../providers/Leads/Leads.provider';
import { useTranslation } from 'react-i18next';
import { SIZE } from 'models/Size';
import Counter, {
  COUNTER_VARIANTS,
} from 'components/Counter/Counter.component';
import useLead from '../../hooks/useLead';
import { getLeadsHeaderCounts, LeadsHeaderCounts } from '../../utils';
import DisplayFilesByLeadPopupProvider from '../../providers/DisplayFilesByLeadPopup/DisplayFilesByLeadPopup.provider';
import LeadsFilterControls from './components/LeadsFilterControls';
import useLeadsFilterOptions from './components/LeadsFilterControls/useLeadsFilterOptions';
import { getIsArrayEmpty } from 'utils';
import ManageColumns from 'components/ManageColumns';

import './LeadsTableContainer.styles.scss';

type LeadsTableContainerProps = {
  className?: string;
};

const LeadsTableContainer: React.FC<LeadsTableContainerProps> = (props) => {
  const { className } = props;

  const {
    showInactiveLeads,
    totalOngoingPages,
    totalOngoingItems,
    onGoingTableLeads,
    waitlistedTableLeads,
    totalWaitlistedPages,
    totalWaitlistedItems,
    isLoadingOngoingTable,
    isLoadingWaitlistedTable,
    ongoingLeads,
    waitlistedLeads,
    numberOfWaitlistedGridLeads,
    showFileAccessSettings,
    updateLead,
    removeLead,
    openLeadDrawer,
    openReorderFilesDrawer,
  } = useContext(LeadsContext);
  const { isSuperAdmin } = useContext(CurrentUserContext);

  const leadTableDefaultColumns = useMemo(
    () => (isSuperAdmin ? SUPER_ADMIN_COLUMNS : TABLE_COLUMNS),
    [isSuperAdmin],
  );

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onReorder,
    onColumnSizingChange,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.LEAD,
    leadTableDefaultColumns,
  );

  const manageColumns = useMemo(
    () => (
      <ManageColumns
        allColumns={allColumns}
        hiddenColumns={hiddenColumns}
        headingTranslations={HEADING_TRANSLATIONS}
        lockedColumn={REQUIRED_COLUMN}
        isDefaultColumns={isDefaultColumns}
        onSave={saveColumnSettings}
        onResetToDefault={resetToDefault}
      />
    ),
    [
      allColumns,
      hiddenColumns,
      isDefaultColumns,
      resetToDefault,
      saveColumnSettings,
    ],
  );

  const classes = classNames('leads-table-container', className);

  const { t } = useTranslation();

  const {
    params: { inactiveSort },
    setQueryParam,
  } = useQueryParams();

  const { getBackgroundColor } = useLead();

  const { columnFilterOptions: ongoingColumnFilterOptions } =
    useLeadsFilterOptions();

  const { columnFilterOptions: inactiveColumnFilterOptions } =
    useLeadsFilterOptions('inactive', false);

  const [ongoingTableRef, scrollOngoingToStart] = useScrollIntoView();
  const [waitlistedTableRef, scrollWaitlistedToStart] = useScrollIntoView();

  const updateInactiveSort = useCallback(
    (value: string) => setQueryParam('inactiveSort', value),
    [setQueryParam],
  );

  // Prevent the global ScrollTo provider from jumping the page to the top on
  // any query-param change (sort, page, ...) while these tables are mounted.
  // Pagination instead scrolls to the top of the relevant table (below).
  useLockScrollOnParamsReroute();

  const isOngoingEmpty =
    !isLoadingOngoingTable && getIsArrayEmpty(onGoingTableLeads);

  const isWaitlistedEmpty =
    !isLoadingWaitlistedTable && getIsArrayEmpty(waitlistedTableLeads);

  const ongoingCounts = useMemo(
    () => getLeadsHeaderCounts(ongoingLeads),
    [ongoingLeads],
  );

  const waitlistedCounts = useMemo(
    () => getLeadsHeaderCounts(waitlistedLeads),
    [waitlistedLeads],
  );

  const renderCountHeader = useCallback(
    (
      unreadCount: number,
      unrepliedCount: number,
      label: string,
      wrapperClassName: string,
    ) => (
      <div className={wrapperClassName}>
        <Counter
          count={unreadCount || unrepliedCount}
          isPositionAbsolute
          size={SIZE.SMALL}
          variant={
            unreadCount ? COUNTER_VARIANTS.ORANGE : COUNTER_VARIANTS.SILVER
          }
        >
          {unreadCount || unrepliedCount}
        </Counter>

        {label}
      </div>
    ),
    [],
  );

  const buildHeaderTransformations = useCallback(
    (counts: LeadsHeaderCounts): Record<string, ReactNode> => ({
      chat: () =>
        renderCountHeader(
          counts.chatUnread,
          counts.chatUnreplied,
          t('chat'),
          'lds-chat-header',
        ),
      notes: () =>
        renderCountHeader(
          counts.notesUnread,
          counts.notesUnreplied,
          t('notes'),
          'lds-notes-header',
        ),
    }),
    [renderCountHeader, t],
  );

  const ongoingHeaderTransformations = useMemo(
    () => buildHeaderTransformations(ongoingCounts),
    [buildHeaderTransformations, ongoingCounts],
  );

  const waitlistedHeaderTransformations = useMemo(
    () => buildHeaderTransformations(waitlistedCounts),
    [buildHeaderTransformations, waitlistedCounts],
  );

  const tableContentTransformations = useMemo(
    () =>
      leadTableDefaultColumns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (lead: Lead) =>
            BODY_CONTENT_GENERATE[col]({ lead, updateLead, removeLead }),
        }),
        {},
      ),
    [leadTableDefaultColumns, removeLead, updateLead],
  );

  return (
    <DisplayFilesByLeadPopupProvider>
      <div className={classes}>
        <LeadsFilterControls
          columns={columns}
          pageParam={ONGOING_PAGE}
          isEmpty={isOngoingEmpty}
          manageColumns={manageColumns}
        />

        <div ref={ongoingTableRef}>
          <ReorderResizeTable
            columns={columns as Array<keyof Lead>}
            sortingValues={SORTING_VALUES}
            headingTranslations={HEADING_TRANSLATIONS}
            columnFilterOptions={ongoingColumnFilterOptions}
            isDataLoading={isLoadingOngoingTable}
            data={onGoingTableLeads}
            settingsValues={
              showFileAccessSettings
                ? ({
                    fileAccess: openReorderFilesDrawer,
                  } as any)
                : []
            }
            customSizes={CUSTOM_COL_SIZES}
            showPlaceholder={false}
            tableHeaderContentTransformations={ongoingHeaderTransformations}
            tableContentTransformations={tableContentTransformations}
            pageParam={ONGOING_PAGE}
            totalPages={totalOngoingPages}
            totalItems={totalOngoingItems}
            recordName="lead"
            getRowBackgroundColor={getBackgroundColor}
            columnSizing={columnSizing}
            onColumnSizingChange={onColumnSizingChange}
            onReorder={onReorder}
            onRowClick={openLeadDrawer}
            onPaginationClick={scrollOngoingToStart}
          />
        </div>

        <div className="leads-table-container__inactive-toggle">
          <ShowInactiveLeads />

          {showInactiveLeads && !!numberOfWaitlistedGridLeads && (
            <LeadsFilterControls
              columns={columns}
              sortParam="inactiveSort"
              filterParamPrefix="inactive"
              pageParam={INACTIVE_PAGE}
              hasStageFilter={false}
              isEmpty={isWaitlistedEmpty}
              manageColumns={manageColumns}
            />
          )}
        </div>

        {showInactiveLeads && !!numberOfWaitlistedGridLeads && (
          <div ref={waitlistedTableRef}>
            <ReorderResizeTable
              tableContentTransformations={tableContentTransformations}
              tableHeaderContentTransformations={
                waitlistedHeaderTransformations
              }
              columns={columns as Array<keyof Lead>}
              sortingValues={SORTING_VALUES}
              headingTranslations={HEADING_TRANSLATIONS}
              columnFilterOptions={inactiveColumnFilterOptions}
              sort={inactiveSort}
              updateSort={updateInactiveSort}
              isDataLoading={isLoadingWaitlistedTable}
              data={waitlistedTableLeads}
              customSizes={CUSTOM_COL_SIZES}
              showPlaceholder={false}
              pageParam={INACTIVE_PAGE}
              totalPages={totalWaitlistedPages}
              totalItems={totalWaitlistedItems}
              recordName="lead"
              getRowBackgroundColor={getBackgroundColor}
              columnSizing={columnSizing}
              onColumnSizingChange={onColumnSizingChange}
              onReorder={onReorder}
              onRowClick={openLeadDrawer}
              onPaginationClick={scrollWaitlistedToStart}
            />
          </div>
        )}
      </div>
    </DisplayFilesByLeadPopupProvider>
  );
};

export default LeadsTableContainer;
